import type { Locale } from '@multicrm/shared';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Небольшой словарь вместо библиотеки i18n: строк немного, а лишние 15 КБ
 * в бандле мини-аппа заметны. Основной язык — украинский (на нём работает
 * магазин), русский задан как набор замен.
 */
const uk = {
  'app.title': 'CRM',
  'app.loading': 'Завантаження…',
  'app.retry': 'Спробувати ще',
  'app.cancel': 'Скасувати',
  'app.save': 'Зберегти',
  'app.saved': 'Збережено',
  'app.close': 'Закрити',
  'app.copy': 'Скопіювати',
  'app.copied': 'Скопійовано',
  'app.search': 'Пошук',
  'app.all': 'Усі',
  'app.offline': 'Немає зв’язку з сервером',
  'app.live': 'Онлайн',
  'app.reconnecting': 'Перепідключення…',
  'app.nothingFound': 'Нічого не знайдено',
  'app.loadMore': 'Показати ще',

  'gate.openInTelegram': 'Відкрийте застосунок через Telegram',
  'gate.openInTelegramHint':
    'Це Telegram Mini App: він працює лише всередині Telegram, бо саме звідти приходить підтвердження, хто ви.',
  'gate.denied': 'Доступ закрито',
  'gate.deniedHint':
    'Ваш Telegram ID не в списку допущених. Попросіть власника додати його — ID можна дізнатися командою /whoami у бота.',
  'gate.authError': 'Не вдалося увійти',

  'nav.orders': 'Замовлення',
  'nav.customers': 'Клієнти',
  'nav.finance': 'Фінанси',
  'nav.settings': 'Налаштування',

  'orders.title': 'Замовлення',
  'orders.searchPlaceholder': 'Номер, телефон, ТТН, місто',
  'orders.empty': 'Замовлень поки немає',
  'orders.emptyFiltered': 'За цим фільтром замовлень немає',
  'orders.newOrder': 'Нове замовлення',
  'orders.itemsCount': 'позицій',
  'orders.partial': 'Частина магазинів не відповіла',

  'order.title': 'Замовлення',
  'order.changeStatus': 'Змінити статус',
  'order.statusSaved': 'Статус змінено',
  'order.statusWarning': 'Зміна статусу надсилає клієнту SMS',
  'order.customer': 'Клієнт',
  'order.delivery': 'Доставка',
  'order.items': 'Товари',
  'order.payment': 'Оплата',
  'order.comment': 'Коментар',
  'order.attribution': 'Джерело',
  'order.tracking': 'ТТН',
  'order.setTracking': 'Записати ТТН',
  'order.trackingPlaceholder': 'Номер накладної',
  'order.trackingSaved': 'ТТН записано',
  'order.trackingStatus': 'Статус посилки',
  'order.paidOnline': 'Оплачено онлайн',
  'order.remaining': 'Залишок',
  'order.createLink': 'Створити платіжне посилання',
  'order.openCustomer': 'Картка клієнта',
  'order.noCall': 'Не телефонувати',
  'order.nonPickup': 'Не забирає посилки',
  'order.discount': 'Знижка',
  'order.created': 'Створено',
  'order.updated': 'Оновлено',
  'order.notFound': 'Замовлення не знайдено',
  'order.edit': 'Редагувати',
  'order.nameRequired': 'Вкажіть імʼя та прізвище',
  'order.phoneInvalid': 'Некоректний телефон',
  'order.edit.noChanges': 'Немає змін для збереження',
  'order.edit.legacyItems':
    'Позиції цього замовлення перенесені зі старої CRM. Змінити їх тут не можна, щоб не втратити назви й ціни.',
  'order.edit.totalHint': 'Остаточну суму порахує магазин за поточними цінами',
  'order.edit.notAllowed': 'Редагування цього замовлення недоступне',
  'order.edit.priceMissing': 'У позиції немає ціни — її не можна зберегти',

  'customers.title': 'Клієнти',
  'customers.searchPlaceholder': 'Телефон або імʼя',
  'customers.onlyNonPickup': 'Тільки ті, хто не забирає',
  'customers.orders': 'замовлень',
  'customers.spent': 'на суму',
  'customers.empty': 'Клієнтів не знайдено',
  'customer.cashback': 'Кешбек',
  'customer.notes': 'Нотатки менеджера',
  'customer.notesPlaceholder': 'Що важливо знати про цього клієнта',
  'customer.history': 'Історія замовлень',
  'customer.lastOrder': 'Останнє замовлення',

  'finance.title': 'Фінанси',
  'finance.income': 'Доходи',
  'finance.expense': 'Витрати',
  'finance.balance': 'Баланс',
  'finance.byCategory': 'За категоріями',
  'finance.entries': 'Записи',
  'finance.addEntry': 'Додати запис',
  'finance.allTime': 'За весь час',
  'finance.empty': 'Записів за цей період немає',
  'finance.notSupported': 'Цей магазин не веде облік доходів і витрат',

  'settings.title': 'Налаштування',
  'settings.appearance': 'Вигляд',
  'settings.theme': 'Тема',
  'settings.themeSystem': 'Як у Telegram',
  'settings.themeLight': 'Світла',
  'settings.themeDark': 'Темна',
  'settings.language': 'Мова',
  'settings.notifications': 'Повідомлення',
  'settings.notifyPersonal': 'Особисті',
  'settings.notifyGroup': 'Група',
  'settings.notifyPersonalHint': 'Сповіщення, які надсилаються в особистий чат з ботом',
  'settings.notifyGroupHint': 'Сповіщення, які бот надсилає у вибрану групу',
  'settings.notificationsEnabled': 'Надсилати повідомлення',
  'settings.newOrders': 'Нові замовлення',
  'settings.statusChanges': 'Зміни статусу',
  'settings.trackingChanges': 'Статус посилки',
  'settings.payments': 'Оплати',
  'settings.storeStatus': 'Доступність сайтів (онлайн / офлайн)',
  'settings.silentMessages': 'Без звуку в Telegram',
  'settings.silentMessagesHint': 'Сповіщення надходитимуть без звуку та вібрації',
  'settings.quietHours': 'Тихі години (не турбувати)',
  'settings.quietHoursHint': 'Не турбувати у вибраний період (крім збоїв сайтів)',
  'settings.quietFrom': 'З',
  'settings.quietTo': 'До',
  'settings.storeFilter': 'Фільтр магазинів для сповіщень',
  'settings.storeFilterHint': 'Отримувати сповіщення лише за вибраними магазинами',
  'settings.allStores': 'Всі магазини',
  'settings.stores': 'Магазини',
  'settings.homeScreen': 'Іконка на екрані',
  'settings.addToHomeScreen': 'Додати',
  'settings.homeScreenAdded': 'Додано',
  'settings.homeScreenHint': 'Швидкий запуск CRM з головного екрана смартфона',
  'settings.doubleTapHint': 'Подвійний клік — показати лише цей магазин',
  'settings.users': 'Користувачі CRM',
  'settings.groups': 'Підключені групи Telegram',
  'settings.groupConnected': 'Бот активний у групі',
  'settings.groupRole': 'Груповий чат',
  'settings.usersAccess': 'Доступ до застосунку',
  'settings.userOnline': 'У мережі',
  'settings.userAwaiting': 'Очікує першого входу',
  'settings.sessions': 'Активні сесії',
  'settings.sessionsRevoke': 'Вийти на інших пристроях',
  'settings.sessionsRevoked': 'Інші сесії закрито',
  'settings.sync': 'Синхронізація',
  'settings.syncEvery': 'Опитування кожні',
  'settings.lastSync': 'Останній обмін',
  'settings.account': 'Акаунт',
  'settings.role': 'Роль',
  'settings.logout': 'Вийти',
  'settings.version': 'Версія',

  'store.online': 'Працює',
  'store.degraded': 'З перебоями',
  'store.offline': 'Не відповідає',
  'store.unknown': 'Невідомо',

  'role.owner': 'Власник',
  'role.manager': 'Менеджер',
  'role.viewer': 'Спостерігач',

  'error.capability': 'Магазин так не вміє',
  'error.conflict': 'Хтось змінив це раніше — оновіть сторінку',
  'error.cloudflare': 'Cloudflare блокує запити з сервера',
  'error.storeUnavailable': 'Магазин недоступний',
  'error.forbidden': 'Недостатньо прав',
  'error.unknown': 'Щось пішло не так',

  'nav.products': 'Товари',

  'newOrder.title': 'Нове замовлення',
  'newOrder.store': 'Магазин',
  'newOrder.items': 'Товари',
  'newOrder.addItem': 'Додати товар',
  'newOrder.searchProduct': 'Назва або артикул',
  'newOrder.recipient': 'Отримувач',
  'newOrder.firstName': 'Імʼя',
  'newOrder.lastName': 'Прізвище',
  'newOrder.phone': 'Телефон',
  'newOrder.delivery': 'Доставка',
  'newOrder.city': 'Місто',
  'newOrder.branch': 'Відділення',
  'newOrder.searchCity': 'Почніть вводити місто',
  'newOrder.searchBranch': 'Номер або адреса відділення',
  'newOrder.payment': 'Оплата',
  'newOrder.discount': 'Знижка, %',
  'newOrder.comment': 'Коментар до замовлення',
  'newOrder.skipSms': 'Не надсилати SMS про прийом',
  'newOrder.total': 'До сплати',
  'newOrder.create': 'Створити замовлення',
  'newOrder.created': 'Замовлення створено',
  'newOrder.noStores': 'Жоден підключений магазин не вміє створювати замовлення',
  'newOrder.emptyItems': 'Додайте хоча б один товар',
  'newOrder.quantity': 'Кількість',
  'newOrder.priceUnknown': 'Ціна невідома',
  'newOrder.manualAddress': 'Цей магазин не має довідника — введіть адресу вручну',

  'risk.new': 'Новий клієнт',
  'risk.orders': 'замовлень',
  'risk.refusals': 'відмов',
  'risk.turnover': 'оборот',

  'sms.title': 'SMS клієнту',
  'sms.placeholder': 'Текст повідомлення',
  'sms.send': 'Надіслати SMS',
  'sms.sent': 'SMS надіслано',
  'sms.ttnTemplate': 'ТТН вашого замовлення: ',

  'payment.amount': 'Сума',
  'payment.linkCreated': 'Посилання створено',
  'payment.copyLink': 'Скопіювати посилання',
  'payment.remainingHint': 'Не більше залишку',

  'products.title': 'Товари',
  'products.search': 'Назва або артикул',
  'products.empty': 'Товарів не знайдено',
  'products.hint': 'Введіть щонайменше 2 символи',
  'products.inStock': 'В наявності',
  'products.outOfStock': 'Немає',
  'products.stock': 'Залишок',
  'products.editStock': 'Змінити залишок',
  'products.stockSaved': 'Залишок оновлено',
  'products.notSupported': 'Цей магазин не дає шукати товари',
} as const;

export type MessageKey = keyof typeof uk;

const ru: Partial<Record<MessageKey, string>> = {
  'app.loading': 'Загрузка…',
  'app.retry': 'Попробовать снова',
  'app.cancel': 'Отмена',
  'app.save': 'Сохранить',
  'app.saved': 'Сохранено',
  'app.close': 'Закрыть',
  'app.copy': 'Скопировать',
  'app.copied': 'Скопировано',
  'app.search': 'Поиск',
  'app.all': 'Все',
  'app.offline': 'Нет связи с сервером',
  'app.live': 'Онлайн',
  'app.reconnecting': 'Переподключение…',
  'app.nothingFound': 'Ничего не найдено',
  'app.loadMore': 'Показать ещё',

  'gate.openInTelegram': 'Откройте приложение через Telegram',
  'gate.openInTelegramHint':
    'Это Telegram Mini App: он работает только внутри Telegram, потому что именно оттуда приходит подтверждение, кто вы.',
  'gate.denied': 'Доступ закрыт',
  'gate.deniedHint':
    'Вашего Telegram ID нет в списке допущенных. Попросите владельца добавить его — ID можно узнать командой /whoami у бота.',
  'gate.authError': 'Не удалось войти',

  'nav.orders': 'Заказы',
  'nav.customers': 'Клиенты',
  'nav.finance': 'Финансы',
  'nav.settings': 'Настройки',

  'orders.title': 'Заказы',
  'orders.searchPlaceholder': 'Номер, телефон, ТТН, город',
  'orders.empty': 'Заказов пока нет',
  'orders.emptyFiltered': 'По этому фильтру заказов нет',
  'orders.newOrder': 'Новый заказ',
  'orders.itemsCount': 'позиций',
  'orders.partial': 'Часть магазинов не ответила',

  'order.title': 'Заказ',
  'order.changeStatus': 'Изменить статус',
  'order.statusSaved': 'Статус изменён',
  'order.statusWarning': 'Смена статуса отправляет клиенту SMS',
  'order.customer': 'Клиент',
  'order.delivery': 'Доставка',
  'order.items': 'Товары',
  'order.payment': 'Оплата',
  'order.comment': 'Комментарий',
  'order.attribution': 'Источник',
  'order.trackingStatus': 'Статус посылки',
  'order.setTracking': 'Записать ТТН',
  'order.trackingPlaceholder': 'Номер накладной',
  'order.trackingSaved': 'ТТН записан',
  'order.paidOnline': 'Оплачено онлайн',
  'order.remaining': 'Остаток',
  'order.createLink': 'Создать платёжную ссылку',
  'order.openCustomer': 'Карточка клиента',
  'order.noCall': 'Не звонить',
  'order.nonPickup': 'Не забирает посылки',
  'order.discount': 'Скидка',
  'order.created': 'Создан',
  'order.updated': 'Обновлён',
  'order.notFound': 'Заказ не найден',

  'customers.title': 'Клиенты',
  'customers.searchPlaceholder': 'Телефон или имя',
  'customers.onlyNonPickup': 'Только те, кто не забирает',
  'customers.orders': 'заказов',
  'customers.spent': 'на сумму',
  'customers.empty': 'Клиенты не найдены',
  'customer.cashback': 'Кешбэк',
  'customer.notes': 'Заметки менеджера',
  'customer.notesPlaceholder': 'Что важно знать об этом клиенте',
  'customer.history': 'История заказов',
  'customer.lastOrder': 'Последний заказ',

  'finance.title': 'Финансы',
  'finance.income': 'Доходы',
  'finance.expense': 'Расходы',
  'finance.balance': 'Баланс',
  'finance.byCategory': 'По категориям',
  'finance.entries': 'Записи',
  'finance.addEntry': 'Добавить запись',
  'finance.allTime': 'За всё время',
  'finance.empty': 'Записей за этот период нет',
  'finance.notSupported': 'Этот магазин не ведёт учёт доходов и расходов',

  'settings.title': 'Настройки',
  'settings.appearance': 'Внешний вид',
  'settings.theme': 'Тема',
  'settings.themeSystem': 'Как в Telegram',
  'settings.themeLight': 'Светлая',
  'settings.themeDark': 'Тёмная',
  'settings.language': 'Язык',
  'settings.notifications': 'Уведомления',
  'settings.notifyPersonal': 'Личные',
  'settings.notifyGroup': 'Группа',
  'settings.notifyPersonalHint': 'Уведомления, которые приходят в личный диалог с ботом',
  'settings.notifyGroupHint': 'Уведомления, которые бот отправляет в выбранную группу',
  'settings.notificationsEnabled': 'Присылать уведомления',
  'settings.newOrders': 'Новые заказы',
  'settings.statusChanges': 'Изменения статуса',
  'settings.trackingChanges': 'Статус посылки',
  'settings.payments': 'Оплаты',
  'settings.storeStatus': 'Доступность сайтов (онлайн / офлайн)',
  'settings.silentMessages': 'Без звука в Telegram',
  'settings.silentMessagesHint': 'Уведомления будут приходить без звука и вибрации',
  'settings.quietHours': 'Тихие часы (не беспокоить)',
  'settings.quietHoursHint': 'Не беспокоить в выбранный период (кроме сбоев сайтов)',
  'settings.quietFrom': 'С',
  'settings.quietTo': 'До',
  'settings.storeFilter': 'Фильтр магазинов для уведомлений',
  'settings.storeFilterHint': 'Получать уведомления только по выбранным магазинам',
  'settings.allStores': 'Все магазины',
  'settings.stores': 'Магазины',
  'settings.homeScreen': 'Иконка на экране',
  'settings.addToHomeScreen': 'Добавить',
  'settings.homeScreenAdded': 'Добавлено',
  'settings.homeScreenHint': 'Быстрый запуск CRM с главного экрана смартфона',
  'settings.doubleTapHint': 'Двойной клик — показать только этот магазин',
  'settings.users': 'Пользователи CRM',
  'settings.groups': 'Подключенные группы Telegram',
  'settings.groupConnected': 'Бот активен в группе',
  'settings.groupRole': 'Групповой чат',
  'settings.usersAccess': 'Доступ к приложению',
  'settings.userOnline': 'В сети',
  'settings.userAwaiting': 'Ожидает первого входа',
  'settings.sessions': 'Активные сессии',
  'settings.sessionsRevoke': 'Выйти на других устройствах',
  'settings.sessionsRevoked': 'Другие сессии закрыты',
  'settings.sync': 'Синхронизация',
  'settings.syncEvery': 'Опрос каждые',
  'settings.lastSync': 'Последний обмен',
  'settings.account': 'Аккаунт',
  'settings.role': 'Роль',
  'settings.logout': 'Выйти',
  'settings.version': 'Версия',

  'store.online': 'Работает',
  'store.degraded': 'С перебоями',
  'store.offline': 'Не отвечает',
  'store.unknown': 'Неизвестно',

  'role.owner': 'Владелец',
  'role.manager': 'Менеджер',
  'role.viewer': 'Наблюдатель',

  'error.capability': 'Магазин так не умеет',
  'error.conflict': 'Кто-то изменил это раньше — обновите страницу',
  'error.cloudflare': 'Cloudflare блокирует запросы с сервера',
  'error.storeUnavailable': 'Магазин недоступен',
  'error.forbidden': 'Недостаточно прав',
  'error.unknown': 'Что-то пошло не так',
  'order.edit': 'Редактировать',
  'order.edit.noChanges': 'Нет изменений для сохранения',
  'order.edit.legacyItems':
    'Позиции этого заказа перенесены из старой CRM. Менять их здесь нельзя, чтобы не потерять названия и цены.',
  'order.edit.totalHint': 'Итоговую сумму посчитает магазин по текущим ценам',
  'order.edit.notAllowed': 'Редактирование этого заказа недоступно',
  'order.edit.priceMissing': 'У позиции нет цены — её нельзя сохранить',

  'nav.products': 'Товары',

  'newOrder.title': 'Новый заказ',
  'newOrder.store': 'Магазин',
  'newOrder.items': 'Товары',
  'newOrder.addItem': 'Добавить товар',
  'newOrder.searchProduct': 'Название или артикул',
  'newOrder.recipient': 'Получатель',
  'newOrder.firstName': 'Имя',
  'newOrder.lastName': 'Фамилия',
  'newOrder.phone': 'Телефон',
  'newOrder.delivery': 'Доставка',
  'newOrder.city': 'Город',
  'newOrder.branch': 'Отделение',
  'newOrder.searchCity': 'Начните вводить город',
  'newOrder.searchBranch': 'Номер или адрес отделения',
  'newOrder.payment': 'Оплата',
  'newOrder.discount': 'Скидка, %',
  'newOrder.comment': 'Комментарий к заказу',
  'newOrder.skipSms': 'Не отправлять SMS о приёме',
  'newOrder.total': 'К оплате',
  'newOrder.create': 'Создать заказ',
  'newOrder.created': 'Заказ создан',
  'newOrder.noStores': 'Ни один подключённый магазин не умеет создавать заказы',
  'newOrder.emptyItems': 'Добавьте хотя бы один товар',
  'newOrder.quantity': 'Количество',
  'newOrder.priceUnknown': 'Цена неизвестна',
  'newOrder.manualAddress': 'У этого магазина нет справочника — введите адрес вручную',

  'risk.new': 'Новый клиент',
  'risk.orders': 'заказов',
  'risk.refusals': 'отказов',
  'risk.turnover': 'оборот',

  'sms.title': 'SMS клиенту',
  'sms.placeholder': 'Текст сообщения',
  'sms.send': 'Отправить SMS',
  'sms.sent': 'SMS отправлено',
  'sms.ttnTemplate': 'ТТН вашего заказа: ',

  'payment.amount': 'Сумма',
  'payment.linkCreated': 'Ссылка создана',
  'payment.copyLink': 'Скопировать ссылку',
  'payment.remainingHint': 'Не больше остатка',

  'products.title': 'Товары',
  'products.search': 'Название или артикул',
  'products.empty': 'Товары не найдены',
  'products.hint': 'Введите минимум 2 символа',
  'products.inStock': 'В наличии',
  'products.outOfStock': 'Нет',
  'products.stock': 'Остаток',
  'products.editStock': 'Изменить остаток',
  'products.stockSaved': 'Остаток обновлён',
  'products.notSupported': 'Этот магазин не даёт искать товары',
};

const STORAGE_KEY = 'multicrm.locale';

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function readLocale(fallback: Locale): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'uk' || stored === 'ru') return stored;
  } catch {
    // хранилище недоступно
  }
  return fallback;
}

export function I18nProvider({
  children,
  initialLocale = 'uk',
}: {
  children: ReactNode;
  initialLocale?: Locale;
}): ReactNode {
  const [locale, setLocaleState] = useState<Locale>(() => readLocale(initialLocale));

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // не критично
    }
    document.documentElement.lang = next;
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      // Отсутствующий русский перевод подменяется украинским, а не пустотой.
      t: (key) => (locale === 'ru' ? (ru[key] ?? uk[key]) : uk[key]),
    }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n вне I18nProvider');
  return context;
}
